
import { useState } from 'react';
import { useCustomerFetching } from './useCustomerFetching';
import { useCustomerSelection } from '../useCustomerSelection';
import { useCustomerOperations } from './useCustomerOperations';
import { Customer, CustomerStatus } from '@/lib/supabase/customers/types/CustomerTypes';
import { formatDate, formatPhone } from './utils';

interface UseCustomersDataProps {
  shopId?: string;
  status?: CustomerStatus;
}

export const useCustomersData = ({ shopId, status = 'all' }: UseCustomersDataProps = {}) => {
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  
  // Customer fetching hook
  const { 
    customers, 
    filteredCustomers, 
    loading, 
    fetchCustomers,
    organizationId
  } = useCustomerFetching({ shopId, status });
  
  // Customer selection hook - only pass filtered customers to avoid reset issues
  const {
    selectedCustomers,
    toggleCustomerSelection,
    toggleSelectAll,
    areAllSelected
  } = useCustomerSelection(filteredCustomers as Customer[]);
  
  // Customer operations hook (add, edit, delete)
  const {
    deleting,
    handleAddCustomer,
    handleEditCustomer,
    handleDeleteCustomer,
    handleBulkDeleteCustomers,
    shopId: selectedShopId
  } = useCustomerOperations({
    organizationId,
    fetchCustomers,
    setEditingCustomerId,
    customers: filteredCustomers as Customer[],
    shopId
  });

  // Handle customer updated
  const handleCustomerUpdated = () => {
    fetchCustomers();
    setEditingCustomerId(null);
  };

  // Create a specific bulk delete handler that takes the selectedCustomers directly
  const handleBulkDelete = () => {
    if (selectedCustomers.length > 0) {
      handleBulkDeleteCustomers(selectedCustomers);
    }
  };

  return {
    customers: filteredCustomers as Customer[],
    loading,
    selectedCustomers,
    editingCustomerId,
    deleting,
    handleAddCustomer,
    handleEditCustomer,
    handleDeleteCustomer,
    handleBulkDeleteCustomers: handleBulkDelete,
    fetchCustomers,
    toggleCustomerSelection,
    toggleSelectAll,
    areAllSelected,
    setEditingCustomerId,
    handleCustomerUpdated,
    shopId: selectedShopId,
    formatDate,
    formatPhone
  };
};

// For backward compatibility
export const useClientsData = useCustomersData;

// Re-export utility functions
export { formatDate, formatPhone } from './utils';
